import { fetchWithAuth, BASE_BACKEND_URL } from '../../auth'
import { faCalendar, faFilter, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import DatePicker from 'react-multi-date-picker'
import Toolbar from 'react-multi-date-picker/plugins/toolbar'
import ReactFilter from '../../components/inputs/ReactFilterList'
import LoadingScreen from '../../components/LoadingScreen'
import { DEFAULT_PER_PAGE } from '../../constants/constant'
import { formatMoney } from '../../helpers/utils'
import { LayoutContext } from '../../layout/MainLayout'
import Pagination from '../../layout/Pagination'
import usePagination from '../../layout/usePagination'
import dayjs from '../../utils/dayjsConfig'
import { DATE_PICKER_SETTINGS, DELIVERY_STATUS_DIC, ON_BOARD_SALE } from '../constants/constants'

const DeliveryIndexBoardView = () => {
  const { collapsed } = useContext(LayoutContext)
  const [data, setData] = useState(null)
  const title = 'Venta a bordo'
  const [clientsUid, setClientsUid] = useState([])
  const [filters, setFilters] = useState([])
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [filterVersion, setFilterVersion] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)

  ////// Stops fetchs when page is changed
  const abortControllerRef = React.useRef(new AbortController())

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortControllerRef.current.abort('Fetch Aborted by User.')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      abortControllerRef.current.abort('Component unmounted')
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])
  //////

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])

      const selectedIds = labels.map((label) => labelToIdMapping[label])
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
    buildFiltersQuery()
  }

  const filterMappings = {
    'NOMBRE DE VENDEDOR': 'user_uid',
    'NOMBRE DE CLIENTE': 'client_uid'
  }

  const buildFiltersQuery = () => {
    let queryParam = ''

    allSelectedOptions.forEach((value, key) => {
      if (value.length !== 0) {
        const paramKey = filterMappings[key]
        if (paramKey) {
          let selectedValues = [...value]

          queryParam += `&${paramKey}=${selectedValues.join(',')}`
        }
      }
    })

    setPagination(prev => ({ ...prev, current_page: 1 }))
    setFilterQuery(queryParam)
  }

  const prepareFilterData = () => {
    const localLabelToIdMapping = {}
    if (!clientsUid) {
      return
    }

    const clientOptions = clientsUid
      .filter((client) => (client.client_uid || '').trim() !== '')
      .map((client) => {
        const displayText = `${client.client_name} - ${client.client_uid}`
        localLabelToIdMapping[displayText] = client.client_uid
        return displayText
      })

    const salesmanOptions = Array.from(
      new Set(
        clientsUid
          .filter((client) => (client.client_uid || '').trim() !== '' && client.user_full_name)
          .map((client) => {
            const displayText = `${client.user_uid} - ${client.user_full_name}`
            localLabelToIdMapping[displayText] = client.user_uid
            return displayText
          })
      )
    )

    const newFilters = [
      { key: 'NOMBRE DE VENDEDOR', value: salesmanOptions },
      { key: 'NOMBRE DE CLIENTE', value: clientOptions }
    ]

    setFilters(newFilters)
    setLabelToIdMapping(localLabelToIdMapping)
    setFetchDataStatus({ filters: true })
  }

  const handleDateFilter = (datesObj) => {
    let formatedDates = []
    let queryParam = ''
    let startDate = new Date()
    let endDate = new Date()

    if (!datesObj || datesObj.length === 0) {
      const otherFiltersSelected = Array.from(allSelectedOptions.values()).some(
        (value) => value.length > 0
      )
      setDateQuery(null)
      setSelectedDates(null)
    } else {
      datesObj.map((date) => {
        formatedDates.push(new Date(date.unix * 1000))
      })
      setSelectedDates(datesObj)
      if (formatedDates.length > 1) {
        startDate = formatedDates[0] < formatedDates[1] ? formatedDates[0] : formatedDates[1]
        endDate = formatedDates[0] < formatedDates[1] ? formatedDates[1] : formatedDates[0]
      } else if (formatedDates.length === 1) {
        startDate = formatedDates[0]
        endDate = formatedDates[0]
      }

      const startDateISO = startDate.toISOString()
      const endDateISO = endDate.toISOString()
      queryParam = `&start_date=${startDateISO}&end_date=${endDateISO}`
      setDateQuery(queryParam)
    }
    pagination.current_page = 1
  }

  const resetReactFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setDateQuery(null)
    setSelectedDates(null)
    setSearchInput('')
    setFilterVersion((prevVersion) => prevVersion + 1)
    setTriggerSearch((prev) => !prev)
  }

  useEffect(() => {
    prepareFilterData()
  }, [clientsUid])

  useEffect(() => {
    async function fetchAll() {
      await fetchClientsFromServer()
    }
    fetchAll()
  }, [])

  useEffect(() => {
    if (allSelectedOptions.size > 0) {
      buildFiltersQuery()
    }
  }, [allSelectedOptions])

  const isAnyFilterSelected = () => {
    const filtersSelected = Array.from(allSelectedOptions.entries()).some(
      ([key, value]) => value.length > 0
    )
    const dateSelected = dateQuery !== null && dateQuery.trim() !== ''

    return filtersSelected || dateSelected
  }

  const fetchClientsFromServer = async (loadingState = true) => {
    if (loadingState) {
      setFetchDataStatus({ ...fetchDataStatus, filters: true })
    }
    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/delivery_receipts_onboard?data_filters=true`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      const data = await response
      setClientsUid(data['result'])
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    } finally {
      setFetchDataStatus({ ...fetchDataStatus, filters: false })
    }
  }

  const fetchDataFromServer = useCallback(async () => {
    const per_page = pagination.per_page
    const page = pagination.current_page

    const response = await fetchWithAuth(
      `${BASE_BACKEND_URL}/delivery_receipts_onboard?page=${page}&per_page=${per_page}${filterQuery || dateQuery || searchInput ? '&filter_search=true' : ''}${filterQuery ? filterQuery : ''}${dateQuery ? dateQuery : ''}${searchInput ? `&q=${encodeURIComponent(searchInput)}` : ''}`,
      {
        method: 'GET',
        signal: abortControllerRef.current.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      }
    )

    const data = response
    setPagination((prev) => ({
      ...prev,
      total_pages: Math.ceil(data['count'] / prev.per_page)
    }))
    setData(data['table'])
  }, [pagination.current_page, filterQuery, dateQuery, searchInput])

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
    }

    fetchAll()
  }, [pagination.current_page, filterQuery, dateQuery, triggerSearch])

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
    pagination.current_page = 1
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      pagination.current_page = 1
      fetchDataFromServer()
    }
  }

  const showModel = (event, model) => {
    event.preventDefault()
    const id = model.uuid || model.uid

    window.open(`/ventas_a_bordo/${encodeURIComponent(id)}?type_delivery=${ON_BOARD_SALE.key}`, '_blank')

  }

  const getFormattedDate = (createdDate) => {
    return dayjs(createdDate).format('DD/MM/YYYY hh:mm A')
  }

  const filtersArray = Array.isArray(filters) ? filters : Array.from(filters || [])
  const anyFilterEmpty = filtersArray.some((filter) => filter.value.length === 0)

  if (data === null) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <>
      <div className="index-header detailed-container">
        <h2 className="mb-20">{title}</h2>
      </div>
      <div className="index-table">
        <div className="show-area">
          <h4 className="text-sub-title">Filtrar ventas a bordo por:</h4>
          <div className="filters-search-container mb-10">
            <FontAwesomeIcon className="filter-icon mr-10" icon={faFilter} />
            <div className="filters-container">
              {filters.map((filter, indx) => (
                <div key={`flr-${indx}`} className="mr-10">
                  <ReactFilter
                    className="mr-10"
                    title={filter.key}
                    identifier={filter.key}
                    options={filter.value}
                    resetWatcher={filterVersion}
                    onSelectionChange={updateAllSelectedOptions}
                    disabled={anyFilterEmpty}
                  />
                </div>
              ))}
              <DatePicker
                disabled={anyFilterEmpty}
                range
                rangeHover
                dateSeparator=" - "
                months={DATE_PICKER_SETTINGS.months}
                weekDays={DATE_PICKER_SETTINGS.weekDays}
                format="DD MMM"
                weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
                highlightToday={false}
                numberOfMonths={2}
                maxDate={dayjs().local().endOf('day').toDate()}
                onChange={(dateObjects) => handleDateFilter(dateObjects)}
                value={selectedDates}
                plugins={[
                  <Toolbar
                    position="bottom"
                    names={{
                      deselect: 'Deseleccionar'
                    }}
                    sort={['deselect']}
                  />
                ]}
                render={(value, openCalendar) => {
                  return (
                    <div
                      key={`flr-date`}
                      className={`mr-10 ${anyFilterEmpty ? 'react-filter-disabled' : ''}`}
                    >
                      <div className="react-filter-box filter-input-width-datepicker">
                        <div
                          title="Rango de fechas"
                          onClick={!anyFilterEmpty ? openCalendar : undefined}
                          className="react-filter-box-clickable-zone"
                        >
                          <div className="react-filter-titles">
                            {value?.length > 0 && (
                              <>
                                {Array.isArray(value) ? (
                                  <div className="filter-title-overflow">
                                    {value.join(' - ')}
                                  </div>
                                ) : (
                                  <div className="filter-title-overflow">{value}</div>
                                )}
                                <FontAwesomeIcon
                                  style={{ color: 'ef823a' }}
                                  icon={faCalendar}
                                />
                              </>
                            )}
                            {value?.length == 0 && (
                              <>
                                <div className="filter-title-overflow">RANGO DE FECHAS</div>
                                <FontAwesomeIcon
                                  style={{ color: anyFilterEmpty ? '#D5D5D5' : '#597fa9' }}
                                  icon={faCalendar}
                                />{' '}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }}
              />
            </div>
            <div className="search-input-containter">
              <input
                value={searchInput}
                onChange={handleSearch}
                onKeyDown={handleEnterSearch}
                className="round-search-input"
                style={{ fontSize: 12 }}
                placeholder="Buscar Venta a bordo por # o nombre de cliente"
              />
              {searchInput === '' && (
                <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
              )}
              {searchInput && (
                <div
                  title="Limpiar texto"
                  onClick={resetReactFilters}
                  className="round-search-input-x"
                >
                  X
                </div>
              )}
            </div>
          </div>
          <div className="mb-20">
            {isAnyFilterSelected() && (
              <a onClick={resetReactFilters} className="reset-filter cursor-pointer mb-20">
                Restablecer filtros aplicados
              </a>
            )}
          </div>
          <Table hover responsive className="delivery-table">
            <thead className="table-header-gray">
              <tr>
                <th className="col-date">FECHA DE DOCUMENTO</th>
                <th className="col-seller">VENDEDOR</th>
                <th className="col-client-id">CÓDIGO DE CLIENTE</th>
                <th className="col-client">NOMBRE DE CLIENTE</th>
                <th className="col-doc"># DE DOCUMENTO</th>
                <th className="col-total">TOTAL DE VENTA</th>
                <th className="col-status">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {data && data.length > 0 ? (
                data.map((model, index) => (
                  <tr className="cursor-pointer" key={model.uuid || model.uid}>
                    <td
                      className="td-text-limited td-text-limited-space"
                      id={model.uuid || model.uid}
                      onClick={(event) => showModel(event, model)}
                    >
                      {getFormattedDate(model.created_date)}
                    </td>
                    <td
                      className="td-text-limited"
                      id={model.uuid || model.uid}
                      onClick={(event) => showModel(event, model)}
                    >
                      {model.user_uid} - {model.user_full_name}
                    </td>
                    <td
                      className="td-text-limited"
                      id={model.uuid || model.uid}
                      onClick={(event) => showModel(event, model)}
                    >
                      {model.client_uid}
                    </td>
                    <td
                      className="td-text-limited"
                      id={model.uuid || model.uid}
                      onClick={(event) => showModel(event, model)}
                    >
                      {model.client_name}
                    </td>
                    <td className="td-text-limited">
                      <a
                        href="#"
                        className="ubiqua-link"
                        onClick={(event) => showModel(event, model)}
                      >
                        {model.delivery_reference}
                      </a>
                    </td>
                    <td
                      className="col-total"
                      id={model.uuid || model.uid}
                      onClick={(event) => showModel(event, model)}
                    >
                      {formatMoney(Number(model.total))}
                    </td>

                    <td onClick={(event) => showModel(event, model)}>
                      <div className="td-delivery-status">
                        {DELIVERY_STATUS_DIC[model.status] && (
                          <div className={`order-status ${DELIVERY_STATUS_DIC[model.status].className}`}>
                            {DELIVERY_STATUS_DIC[model.status].label}
                          </div>
                        )}
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    No hay ventas a bordo disponibles en estos momentos.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <nav className="index-table">
        {pagination.total_pages > 1 && (
          <div onClick={handlePaginationClick}>
            <Pagination
              current_page={pagination.current_page}
              total_pages={pagination.total_pages}
            />
          </div>
        )}
      </nav>
    </>

  )
}
export default DeliveryIndexBoardView